import { FeatureTypes } from "entity-app/constants";
import { mergedStrings } from "utils/utils";
import { DateUtils } from "utils/dateutils";

import { specSectionLabel } from "components/cell-renders";
import {
  DropDownDBFeatureInstance,
  DropdownEntityOption,
  ELinkType,
  TEditingEntity,
  TEntityLink,
  TFeatureActivityWithInstance,
  TFeatureDetail,
  TFeatureInstance,
  TLinkSource,
  TSpecSection
} from "../models";

export const getFeatureDetail = (
  featureTypeId: FeatureTypes
): TFeatureDetail => {
  switch (featureTypeId) {
    case FeatureTypes.MATERIALS:
      return {
        featureTypeId,
        feature: "MATERIALS",
        title: "Material",
        titlePlural: "Materials"
      };
      break;
    case FeatureTypes.SUBMITTALS:
      return {
        featureTypeId,
        feature: "SUBMITTALS",
        title: "Submittal",
        titlePlural: "Submittals"
      };
      break;
    case FeatureTypes.ACTIVITIES:
      return {
        featureTypeId,
        feature: "SCHEDULES",
        title: "Activity",
        titlePlural: "Activities"
      };
      break;
    case FeatureTypes.BID_PACKAGE:
      return {
        featureTypeId,
        feature: "BID_PACKAGE",
        title: "Bid Package",
        titlePlural: "Bid Packages"
      };
      break;
    case FeatureTypes.DESIGN_PACKAGE:
      return {
        featureTypeId,
        feature: "DESIGN_PACKAGE",
        title: "Design Package",
        titlePlural: "Design Packages"
      };
      break;
    default:
      return {
        featureTypeId,
        feature: "MATERIALS",
        title: "Entity",
        titlePlural: "Entities"
      };
      break;
  }
};

export const translateFeatureToOptions = (
  data: Array<DropDownDBFeatureInstance>,
  specNumber?: string | null
) => {
  return data.map((item) => {
    const specSection = item.feature_instance_spec_sections[0]?.spec_section;
    const specSectionStr = specSectionLabel({
      specNumber: specSection?.section_number,
      specName: specSection?.title
    });
    const pre = mergedStrings([
      String(item.auto_inc_value || ""),
      specSectionStr
    ]);

    const label = mergedStrings([pre, item.title || ""]).toLocaleLowerCase();
    return {
      isSuggested:
        specNumber && specSection?.section_number
          ? specSection?.section_number === specNumber
          : false,
      pre,
      title: item.title || "",
      label,
      id: item.id,
      isLinked:
        item.links_as_source_aggregate.aggregate.count > 0 ||
        item.links_as_target_aggregate.aggregate.count > 0,
      instance: item
    } as DropdownEntityOption;
  });
};

export const translateEntityInfoIntoBlockCards = ({
  entityInfo,
  leftTabEntityTypes
}: {
  entityInfo: TEntityLink;
  leftTabEntityTypes: FeatureTypes[];
}): TEditingEntity[] => {
  const selectedEntityInfo = { ...entityInfo };
  const specSection: TSpecSection = selectedEntityInfo
    .feature_instance_spec_sections.length
    ? selectedEntityInfo.feature_instance_spec_sections[0]?.spec_section
    : {
        title: null,
        section_number: null
      };
  const entityDetail = {
    id: selectedEntityInfo.id,
    auto_inc_value: selectedEntityInfo.auto_inc_value,
    title: selectedEntityInfo.title,
    specSection
  };
  let shouldHaveMaterialFeature = true;
  if (entityInfo.feature_submittals.length) {
    shouldHaveMaterialFeature =
      entityInfo.feature_submittals[0].material_tracking;
  }

  // Remove if material tracking is OFF for Submittal
  const leftTabFeatures = leftTabEntityTypes.filter(
    (x) => shouldHaveMaterialFeature || x !== FeatureTypes.MATERIALS
  );

  const linkItems = [] as TEditingEntity[];
  leftTabFeatures.forEach((type) => {
    let items = selectedEntityInfo?.links?.filter(
      (item) => item.feature_instance?.feature.id === type
    );

    // Modify title if implicit
    items = items?.map((x) => {
      if (x.feature_instance?.implicit) {
        return {
          ...x,
          feature_instance: {
            ...x.feature_instance,
            title: `Material for Submittal ${selectedEntityInfo.auto_inc_value}`
          }
        };
      }
      return x;
    });

    linkItems.push({
      list:
        items.length > 1 && items.some((x) => x.feature_instance?.implicit)
          ? items.filter((x) => !x.feature_instance?.implicit)
          : items,
      featureDetail: getFeatureDetail(type),
      entityDetail,
      parentEntity: null,
      height: 0
    });
  });
  return linkItems;
};

export const getLinkType = (
  reverse_dependency: boolean,
  isLinkedWithStartdate: boolean
): ELinkType => {
  if (reverse_dependency) {
    return isLinkedWithStartdate
      ? ELinkType.START_TO_START
      : ELinkType.FINISH_TO_START;
  }
  return isLinkedWithStartdate
    ? ELinkType.FINISH_TO_START
    : ELinkType.FINISH_TO_FINISH;
};

export const getLinkedWithStartdate = (
  link_type: ELinkType,
  reverse_dependency: boolean
): boolean => {
  if (link_type === ELinkType.FINISH_TO_FINISH && !reverse_dependency) {
    return false;
  }
  if (link_type === ELinkType.FINISH_TO_START && reverse_dependency) {
    return false;
  }
  return true;
};

export const getTLinkSourceActivityObject = (
  item: TFeatureActivityWithInstance,
  isStartDate: boolean
) => {
  const sourceTargetInstance: TFeatureInstance = {
    id: item.feature_instance_id,
    number: item.feature_instance.number,
    title: item.feature_instance.title,
    auto_inc_value: 0,
    deleted_from_source: false,
    implicit: false,
    feature: {
      id: 9
    },
    feature_workflow_instance: {
      workflow_status: "WF_NOT_STARTED"
    },
    feature_instance_spec_sections: [],
    feature_activities: [item],
    feature_submittals: [],
    links: []
  };
  const sampleObject = {
    governing_link: false,
    created_at: DateUtils.dateTimeObj().toISOString(),
    link_type: getLinkType(false, isStartDate),
    offset_duration: null,
    linked_offset_date: null,
    isLinkedWithStartdate: isStartDate,
    reverse_dependency: false,
    featureInstanceId: item.feature_instance_id,
    feature_instance: sourceTargetInstance
  } as TLinkSource;
  return sampleObject;
};

export const getTLinkSourceMaterialObject = (
  item: DropdownEntityOption
): TLinkSource => {
  const linkSource = item.instance as any;
  const links =
    [...linkSource.links_as_source, ...linkSource.links_as_target].map((x) => ({
      ...x,
      featureInstanceId:
        (x.target_feature_instance || x.source_feature_instance)?.id || "",
      feature_instance: x.target_feature_instance || x.source_feature_instance
    })) || [];

  const sourceInstance: TFeatureInstance = {
    id: item.id,
    auto_inc_value: item.instance.auto_inc_value,
    number: "", // Assign an empty string or a valid string value
    title: item.title,
    deleted_from_source: false,
    implicit: false,
    feature: {
      id: 2
    },
    feature_workflow_instance: {
      workflow_status: "WF_NOT_STARTED"
    },
    feature_instance_spec_sections:
      item.instance.feature_instance_spec_sections,
    feature_activities: [],
    feature_submittals: [],
    links,
    meta: {
      originalLinks: structuredClone(links)
    }
  };
  const sampleObject = {
    governing_link: false,
    created_at: DateUtils.dateTimeObj().toISOString(),
    link_type: ELinkType.FINISH_TO_START,
    offset_duration: null,
    linked_offset_date: null,
    reverse_dependency: false,
    isLinkedWithStartdate: getLinkedWithStartdate(
      ELinkType.FINISH_TO_START,
      false
    ),

    feature_instance: sourceInstance,
    isSuggested: item.isSuggested,
    featureInstanceId: item.id
  } as TLinkSource;
  return sampleObject;
};

export const getTLinkSourceSubmittalObject = (
  item: DropdownEntityOption
): TLinkSource => {
  const sourceInstance = {
    id: item.id,
    auto_inc_value: item.instance.auto_inc_value,
    number: "", // Assign an empty string or a valid string value
    title: item.title,
    deleted_from_source: false,
    implicit: false,
    feature: {
      id: 1
    },
    feature_workflow_instance: {
      workflow_status: "WF_NOT_STARTED"
    },
    feature_instance_spec_sections:
      item.instance.feature_instance_spec_sections,
    feature_activities: [],
    feature_submittals: item.instance.feature_submittals,
    links: []
  } as TFeatureInstance;
  const sampleObject: TLinkSource = {
    governing_link: false,
    created_at: DateUtils.dateTimeObj().toISOString(),
    link_type: ELinkType.FINISH_TO_START,
    offset_duration: null,
    linked_offset_date: null,
    reverse_dependency: false,
    recommended_by_system: false,
    isLinkedWithStartdate: getLinkedWithStartdate(
      ELinkType.FINISH_TO_START,
      false
    ),
    feature_instance: sourceInstance,
    isSuggested: item.isSuggested,
    featureInstanceId: item.id
  };
  return sampleObject;
};

export const hasIntersection = (arr1: string[], arr2: string[]): boolean => {
  const set1 = new Set(arr1);
  return arr2.some((item) => set1.has(item));
};
